import React, { useContext, useEffect } from 'react';
import { PageContext } from '@/context/PageContext';
import { Metadata } from '@latitude/metadata';
import Layout from '@/components/layout';
import { SITE_URL } from '@/utils/constants';
import { ImportantInformation } from '@latitude/important-information-rebrand';
import HeroV2 from '@/components/Hero/HeroV2/HeroV2';
import { REBRAND_COLOR, PADDING } from '@latitude/core/utils/constants';
import Section from '@latitude/section-rebrand';
import { AnalyticsLocationProvider } from 'latitude-analytics';
import { Accordion } from '@latitude/accordion-rebrand';
import Typography from '@latitude/typography';
import HowToApply from '@/components/HowToApplyRebrand/HowToApplyRebrandV2/HowToApplyRebrandV2';
import { Link } from '@latitude/link';
import PageData from '@/data/pages/credit-cards/balance-transfer.json';
import { AnalyticsContext } from 'latitude-analytics';

const IMPORTANT_INFO = [
  "<sup>*</sup> Balance transfer applies to eligible customer's Australian non-Latitude credit cards only. Balance transfer amounts commencing from $500 or more, up to 85% of your participating Latitude credit card’s credit limit. Monthly payments required (exact amounts specified in your statement). Paying only the minimum monthly payment may not pay out the balance transfer amount before the end of the plan period. If there is an outstanding balance after the plan period, interest will be charged at the cash advance rate, currently 29.99% (subject to change). A once-off balance transfer fee of 3% applies to the amount being transferred. This will be charged to your participating Latitude credit card account when the balance transfer is processed. Offer only available to existing customers who have submitted the application form. Eligibility criteria apply. Subject to approval.<br /><br /> <a href='https://assets.latitudefinancial.com/legals/credit-card-general-terms-and-conditions.pdf'>Latitude General Terms and Conditions</a> set out the T&Cs of the participating Latitude credit card. Credit provided by Latitude Finance Australia ABN 42 008 583 588 Australian Credit Licence number 392145. "
];

const HOW_TO_APPLY_DATA = {
  id: 'how-to-apply',
  image: '/images/credit-cards/bt-how-to-apply.png',
  howToApply: {
    sectionTitle: 'How to apply for the Balance Transfer offer',
    items: [
      {
        description:
          '1. Open the Latitude App or visit the Latitude Service Centre by clicking the Log in button, located in the top right corner of the Latitude website. '
      },
      {
        description:
          "2. Log in using your Latitude ID. If you don't have a Latitude ID, simply register from the login page."
      },
      {
        description: '3. Select Balance Transfer and fill in the form. T&Cs apply.'
      },
      {
        description: (<>4. For more information on Terms and Conditions <a href="https://assets.latitudefinancial.com/legals/terms-conditions/balance-transfer-termsconds.pdf" target="_blank">click here.</a></>)
      }
    ]
  }
};

const FREQUENTLY_ASKED_QUESTIONS = [
  {
    q: 'What fees apply to a balance transfer?',
    a: (
      <>
        You will need to pay a once-off balance transfer fee of 3% of the
        balance transfer amount or in accordance with the specific balance
        transfer offer when you take up a promotional offer. This fee is charged
        to your participating Latitude credit card account when your balance
        transfer is processed.
      </>
    )
  },
  {
    q: 'What is my balance transfer limit?',
    a: (
      <>
        The minimum amount is $500 and your total balance transfer amount cannot
        exceed 85% of your credit limit (excluding any applicable balance
        transfer fee charged).
        <br />
        <br />
        For example, if your credit limit is $10,000, the maximum balance
        transfer amount is $8,500. If you have a current balance of $3,000, this
        amount is deducted from $8,500 and as a result your maximum balance
        transfer amount is $5,500.
      </>
    )
  },
  {
    q: 'How much will I have to pay each month?',
    a: (
      <>
        Your monthly statement will specify the minimum amount you will need to
        pay each month. However, if you only make the minimum monthly payment,
        you may not pay out the balance transfer amount before the end of the
        promotional period.
      </>
    )
  },
  {
    q:
      'If I want to make extra or larger payments than the minimum monthly payment toward my balance transfer, how do I do that?',
    a: (
      <>
        You can make extra or larger payments directly toward your balance
        transfer at any time. Open the Latitude App or log in to the Latitude
        Service Centre, go to My Plans, and choose your Balance Transfer. From
        there, enter the amount you want to pay and complete the payment.
      </>
    )
  },
  {
    q:
      'What happens if I have outstanding balance at the end of the promotional period?',
    a: (
      <>
        If there is an outstanding balance after the promotional period,
        interest will be charged at the cash advance rate, currently 29.99%
        (subject to change).
      </>
    )
  },
  {
    q:
      'Does Latitude arrange for the other credit card account(s) to be closed?',
    a: (
      <>
        No. Once Latitude has paid the balance on your other credit card
        account(s), it's your choice whether you close that card account(s).
        Remember, it's a good idea to close other credit card account(s) to
        clear or reduce your credit card debt.
      </>
    )
  },
  {
    q:
      'How long does it take for a balance transfer to be processed to my Latitude credit card account?',
    a: (
      <>
        Balance transfers can take up to 10 business days to process depending
        on the institution(s) to which the payments are being made. Please
        notify us if the balance transfer does not appear on your statement of
        nominated account.
      </>
    )
  },
  {
    q:
      'Can I still use my current Latitude credit card for interest free purchases?',
    a: (
      <>
        If your Latitude credit card has interest free payment plans available
        for purchases, then you can still choose to make these purchases up to
        your available credit limit, after the balance transfer transaction is
        processed.
      </>
    )
  }
];

function GenericBalanceTransferPage({ location }) {
  const contentfulPageData = useContext(PageContext);
  const state = contentfulPageData?.updatedBody || {};
  const [analytics] = useContext(AnalyticsContext);

  useEffect(() => {
    const sourceCode = localStorage.getItem('source_code') || 'IDAAA';
    // adding pageData to analytics
    analytics.pageData = {
      page_pageTitle: 'generic balance transfer',
      page_pageType: 'product-details-page',
      page_siteSection: 'cards',
      page_siteSubsection: 'credit-cards',
      product_productid: ['CCAU28D','CCAUGEM','CCAULRM','CCAUGOM'],
      application_sourcecode: sourceCode || 'IDAAA'
    };
  }, []);

  return (
    <Layout location={location}>
      <main css={`div.how-to-apply-image img {
              transform: scaleX(-1);
        }`}>
        <Metadata
          title={'Generic Balance Transfer'}
          description={
            '3% Balance Transfer fee applies. Balance Transfer cannot be made from a Latitude issued credit card.'
          }
          keywords="balance transfer"
          canonical={`${SITE_URL}${location.pathname}`}
        />

        <HeroV2
          {...(state?.heroBannerData?.[0] || PageData?.contentful?.heroBannerDataGeneric?.[0])}
        />

        <HowToApply data={HOW_TO_APPLY_DATA} background={REBRAND_COLOR.STONE} />

        <Section
          id="faq"
          css={`
            background-color: ${REBRAND_COLOR.WHITE};
            padding-top: ${PADDING.P32};
            padding-bottom: ${PADDING.P32};
          `}
        >
          <div
            data-contentful={state?.faqData?.[0]?.contentfulID}
            css={`
              display: flex;
              flex-direction: column;
              gap: 32px;
            `}
          >
            <Typography.H4>Frequently Asked Questions</Typography.H4>
            <AnalyticsLocationProvider location={'frequently asked questions'}>
              <Accordion
                css={`
                  .bg-black-10 {
                    padding: 16px 24px;
                  }
                `}
                items={state?.faqData?.[0]?.data || FREQUENTLY_ASKED_QUESTIONS}
              />
            </AnalyticsLocationProvider>
          </div>
        </Section>
        <ImportantInformation
          data={{
            content: {
              importantInformationSectionOne: IMPORTANT_INFO
            }
          }}
          css={`
            overflow-x: hidden;
            .row {
              @media (min-width: 1600px) {
                padding-left: ${PADDING.P32};
                padding-right: ${PADDING.P32};
              }
            }
          `}
          {...{
            ...state?.importantInfoData?.[0],
            sectionOneColummClasses: 'col-12 col-lg-6',
            sectionTwoColummClasses: 'col-12 col-lg-6'
          }}
        />
      </main>
    </Layout>
  );
}

export default GenericBalanceTransferPage;